import React, { ComponentProps, useImperativeHandle, useRef } from "react";
import MapGL from "react-map-gl";
import { buildLoginURLWithPrevLocation } from "../../../utils/params";
import { useIsMobileWidth, useIsTabletWidth } from "../../Guest/utils";
import { Viewport } from "../utils/viewportHook";
import {
  BottomRightMapOverlay,
  RightPanelOverlay,
  Shader,
  TopLeftMapOverlay,
  TopRightMapOverlay,
} from "./__styles__/LayeredMap";
import useAccountBoundaryHook from "./accountBoundaryLayer";
import useCbrsHook from "./cbrsLayer";
import useClickObjectMenuHook from "./clickObjectMenuLayer";
import useContourHook from "./contourLayer";
import CustomMapController from "./CustomMapController";
import useCustomMapHook from "./customMapLayer";
import useFirmHook from "./firmLayer";
import useLayeredMapHook from "./layeredMapHook";
import useMarkerHook from "./markerLayer";
import useMeasureHook from "./measureLayer";
import useParcelHook from "./parcelLayer";
import usePropertyHook from "./propertyLayer";
import useRasterHook from "./rasterLayer";
import useSavedViewsClusterHook from "./savedViewsClusterLayer";
import useSavedViewsHook from "./savedViewsLayer";
import TooltipComponent from "./Tooltip";
import type {
  Account,
  CurriedHookBuilder,
  LayerConfigs,
  LayerHookBuilder,
  MapRef,
  ParentLayerNames,
} from "./types";
import useDeviceLocationsHook from "./userDeviceLocationsLayer";

const layerHookBuilders = {
  parcels: useParcelHook,
  marker: useMarkerHook,
  accountBoundary: useAccountBoundaryHook,
  properties: usePropertyHook,
  firms: useFirmHook,
  customMaps: useCustomMapHook,
  savedViews: useSavedViewsHook,
  savedViewsCluster: useSavedViewsClusterHook,
  cbrs: useCbrsHook,
  contour: useContourHook,
  measure: useMeasureHook,
  raster: useRasterHook,
  deviceLocations: useDeviceLocationsHook,
  clickObjectMenu: useClickObjectMenuHook,
};

type Props = {
  layers: LayerConfigs;
  baseMapStyle: Record<string, any>;
  height: string;
  width: string;
  minZoom?: number;
  account: Account;
  viewport: Viewport;
  setViewport: (viewport: Viewport, trigger?: any) => void;
  disableInteraction?: boolean;
  halfMap?: boolean;
  topLeftMapOverlayItems?: Array<React.ReactNode>;
  topRightMapOverlayItems?: Array<React.ReactNode>;
  rightPanelOverlayItems?: Array<React.ReactNode>;
  bottomRightMapOverlayItems?: Array<React.ReactNode>;
  shader?: { hasFeaturedContent: boolean };
} & Omit<ComponentProps<typeof MapGL>, "children">;

export const LayeredMap = React.forwardRef<MapRef, Props>(
  (props, forwardedRef) => {
    const controller = new CustomMapController();
    const ref = useRef<MapRef | null>(null);

    // ideally, we would use useComposedRefs here, but MapGL internally expects a
    // ref object (with a .current property), not a callback ref. instead, we
    // declare an internal ref to pass to the component and then make that ref
    // available on _this_ component via useImperativeHandle
    useImperativeHandle(forwardedRef, () => ref.current!);

    const hookBuilders = Object.entries(props.layers).map(
      ([layerId, config]) => {
        const layerHookBuilder = layerHookBuilders[
          layerId as ParentLayerNames
        ] as LayerHookBuilder<typeof config>;
        const builder: CurriedHookBuilder = callback =>
          callback(layerHookBuilder, config);
        return builder;
      },
    );

    const {
      onClick,
      onHover,
      onLoad,
      onFocusLost,
      tooltip,
      layers,
      cursor,
      interactiveLayerIds,
      onLoadError,
    } = useLayeredMapHook({
      account: props.account,
      hookBuilders,
      map: ref,
    });

    const isMobile = useIsMobileWidth();
    const isTablet = useIsTabletWidth();
    const isDesktop = !isMobile && !isTablet;
    const rightPanelOpen = isDesktop && props.rightPanelOverlayItems?.length;

    // Canvas and map containers should not be focusable for keyboard users
    // Instead, we want to direct them towards search functionality
    const removeCanvasFocus = () => {
      const mapInstance = ref.current?.getMap();

      if (mapInstance) {
        const canvasContainer = mapInstance.getCanvasContainer();
        if (canvasContainer) {
          canvasContainer.setAttribute("tabindex", "-1");
        }

        const canvas = mapInstance.getCanvas();
        if (canvas) {
          canvas.setAttribute("tabindex", "-1");
        }

        const container = mapInstance?.getContainer();

        const tabbableElement = container?.parentElement?.parentElement;
        tabbableElement?.setAttribute("tabindex", "-1");

        const mapboxLogo = container.querySelector("a.mapboxgl-ctrl-logo");
        mapboxLogo?.setAttribute("tabindex", "-1");
      }
    };

    // Combined onLoad handler that calls the original onLoad and removes canvas focus
    const handleMapLoad = () => {
      onLoad();
      removeCanvasFocus();
    };

    return (
      <>
        {props.shader && (
          <Shader hasFeaturedContent={props.shader.hasFeaturedContent} />
        )}
        <TopLeftMapOverlay halfMap={props.halfMap ?? false}>
          {props.topLeftMapOverlayItems?.map((item, index) => (
            <React.Fragment key={`top-left-map-overlay-item-${index}`}>
              {item}
            </React.Fragment>
          ))}
          {!isDesktop &&
            props.rightPanelOverlayItems?.map((item, index) => (
              <React.Fragment key={`right-panel-overlay-item-${index}`}>
                {item}
              </React.Fragment>
            ))}
        </TopLeftMapOverlay>
        {rightPanelOpen ?
          <RightPanelOverlay>
            {props.rightPanelOverlayItems?.map((item, index) => (
              <React.Fragment key={`right-panel-overlay-item-${index}`}>
                {item}
              </React.Fragment>
            ))}
          </RightPanelOverlay>
        : null}
        {!rightPanelOpen ?
          <TopRightMapOverlay>
            {props.topRightMapOverlayItems?.map((item, index) => (
              <React.Fragment key={`top-right-map-overlay-item-${index}`}>
                {item}
              </React.Fragment>
            ))}
          </TopRightMapOverlay>
        : null}

        <MapGL
          ref={ref}
          getCursor={() => cursor}
          mapboxApiAccessToken={window.env.MAPBOX_ACCESS_TOKEN}
          {...props.viewport}
          {...props}
          interactiveLayerIds={interactiveLayerIds}
          attributionControl={false}
          reuseMaps={false}
          onClick={onClick}
          onHover={onHover}
          onMouseOut={onFocusLost}
          controller={controller}
          onLoad={handleMapLoad}
          mapStyle={props.baseMapStyle}
          onError={(e: any) => {
            if (e.error.status === 401) {
              window.location.href = buildLoginURLWithPrevLocation(location);
            } else if (e.error.status === 503) {
              onLoadError(e);
            }
          }}
          onViewportChange={
            !props.disableInteraction ? props.setViewport : () => {}
          }
        >
          {layers}
          {/* the GeolocationTool is required to be rendered with the MapGL, 
            so the entire BottomRightMapOverlay is rendered here */}
          {!rightPanelOpen ?
            <BottomRightMapOverlay halfMap={props.halfMap ?? false}>
              {props.bottomRightMapOverlayItems?.map((item, index) => (
                <React.Fragment key={`bottom-right-map-overlay-item-${index}`}>
                  {item}
                </React.Fragment>
              ))}
            </BottomRightMapOverlay>
          : null}
          <TooltipComponent tooltip={tooltip} />
        </MapGL>
      </>
    );
  },
);
